import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BrandMark } from "@/components/BrandMark";
import { Loader2, CheckCircle2, MailCheck, ArrowRight, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [token, setToken] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); setToken(data.access_token); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) {}
      tries.current += 1;
      if (tries.current > 15) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md stagger-in">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <BrandMark size="h-10 w-10" text="text-xl" />
        </div>
        <Card className="p-8 text-center">
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold">Confirming your payment…</h2>
              <p className="text-sm text-muted-foreground mt-2">This only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold" data-testid="payment-success-heading">Payment confirmed!</h2>
              <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1.5" data-testid="payment-success-email-note"><MailCheck className="h-4 w-4 shrink-0" />We've emailed your course access link to you.</p>
              <p className="text-sm text-muted-foreground mt-1" data-testid="payment-success-spam-note">Please check your junk or spam folder if it's not in your inbox.</p>
              {token && (
                <Button className="mt-6 w-full" onClick={() => navigate(`/access/${token}`)} data-testid="go-to-course-button">
                  Access your course now <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              )}
              <Link to="/shop" className="block text-sm text-muted-foreground hover:text-foreground mt-4">Browse more courses</Link>
            </>
          )}
          {state === "failed" && (
            <>
              <div className="h-14 w-14 rounded-full bg-red-100 dark:bg-red-500/15 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="font-heading text-xl font-bold">We couldn't confirm your payment</h2>
              <p className="text-sm text-muted-foreground mt-2">If you were charged, your access link will still be emailed to you shortly.</p>
              <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/shop")}>Back to shop</Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
